import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GridTableProductPeriod } from './OnlineMarketsGridViewTableProductPeriod';
import { getOrderLineElement, getSingleItem } from '../services/utils';
import { ContractTypes } from '../constants/contract';
import { OrderStatuses } from '../constants/order';

const isValidPeriod = tradingPeriod =>
    /^(January|February|March|April|May|June|July|August|September|October|November|December|Q[1-4]|) [0-9][0-9]$/.test(
        tradingPeriod,
    );
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const ADDITIONAL_PHYSICAL_CONTRACT_PERIODS = ['LOADED', 'PROMPT'];

const populateMonth = (monthIndex, year, includeHalves = true) =>
    includeHalves
        ? [`1H ${MONTHS[monthIndex]} ${year}`, `${MONTHS[monthIndex]} ${year}`, `2H ${MONTHS[monthIndex]} ${year}`]
        : [`${MONTHS[monthIndex]} ${year}`];

const getBasePeriods = (isPhysicalContract, halvesLimit = 3, numRows) => {
    const date = new Date();
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();
    const responsePeriods = new Array(numRows).fill(null);
    const initialPeriods = isPhysicalContract ? ADDITIONAL_PHYSICAL_CONTRACT_PERIODS : [];

    return responsePeriods
        .reduce((periods, item, i) => {
            const monthIndex = (currentMonth + i) % 12;
            const yearOffset = Math.floor((currentMonth + i) / 12);
            const year = (currentYear + yearOffset).toString().slice(2);
            const includeHalves = halvesLimit && i < halvesLimit;
            const monthData = populateMonth(monthIndex, year, includeHalves);
            const quarterData = [2, 5, 8, 11].includes(monthIndex) ? [`Q${(monthIndex + 1) / 3} ${year}`] : [];

            return [...periods, ...monthData, ...quarterData];
        }, initialPeriods)
        .slice(0, numRows)
        .map(period => ({ period, BUY: [], SELL: [] }));
};

const getPeriodString = period => {
    const contract = period.BUY.length ? period.BUY[0] : period.SELL[0];
    const { trading_period, trading_period_prefix: prefix } = contract;

    if (ADDITIONAL_PHYSICAL_CONTRACT_PERIODS.includes(prefix)) {
        return prefix;
    }

    if (!prefix || prefix === '-') {
        return trading_period;
    }

    return `${prefix} ${trading_period}`;
};

const setIsNotExpiredOrTraded = contract =>
    !!(
        (!contract.expiry_time || (contract.expiry_time && new Date(contract.expiry_time) >= new Date())) &&
        contract.status !== OrderStatuses.EXPIRED &&
        contract.status !== OrderStatuses.CLOSED
    );

const fillInPeriodGaps = (product, periods = [], num_rows) => {
    if (!product) {
        return periods;
    }
    const { contract_type } = product;
    const isPhysicalContract = contract_type === ContractTypes.PHYSICAL;
    const halvesLimit = isPhysicalContract ? 3 : null;
    const responsePeriods = getBasePeriods(isPhysicalContract, halvesLimit, num_rows);

    const periodStrings = responsePeriods.map(period => period.period);
    const formattedPerids = periods.map(p => ({
        BUY: p.BUY.filter(setIsNotExpiredOrTraded),
        SELL: p.SELL.filter(setIsNotExpiredOrTraded),
    }));

    formattedPerids.forEach(period => {
        if (period.BUY.length === 0 && period.SELL.length === 0) return;

        const periodString = getPeriodString(period);
        const responsePeriodIndex = periodStrings.indexOf(periodString);
        if (responsePeriodIndex !== -1) {
            responsePeriods[responsePeriodIndex] = { ...responsePeriods[responsePeriodIndex], ...period };
        }
    });

    return responsePeriods;
};

const isIgnoreOrder = order =>
    !isValidPeriod(order.trading_period) && !ADDITIONAL_PHYSICAL_CONTRACT_PERIODS.includes(order.trading_period_prefix);

const GridView = props => {
    const { view, user, data, showOrderPopup, getOrder, placeOrder, callDH, getMarketLog, requestStatusbarData } =
        props;

    const { products } = view.args.include;
    const { num_rows } = view.args;
    const productObjects = user.userInfo.products || [];

    let statusbarPrevId = '';

    const onMouseOver = e => {
        const { products } = user.userInfo;

        const orderLine = getOrderLineElement(e);

        if (orderLine) {
            const product_id = orderLine.getAttribute('productid');
            const product_currency = orderLine.getAttribute('currency');
            const contract_type = orderLine.getAttribute('contract_type');
            const product = getSingleItem(product_id, products);
            const params = {
                currency: product_currency,
                contract_type,
                freights: product.freights,
            };
            props.showStatusbar();

            if (statusbarPrevId != product_id) {
                statusbarPrevId = product_id;
                requestStatusbarData(product_id, params);
            }
        }
    };

    const onMouseOut = e => {
        const orderLine = getOrderLineElement(e);

        if (orderLine && orderLine.getAttribute('name') === 'order_line') {
            props.hideStatusbar();
        }
    };

    const presentedProducts = useMemo(() => {
        return products.reduce((acc, product_id) => {
            const product = productObjects.find(p => p.id === product_id);
            if (product) {
                acc[product.short_name] = product;
            }
            return acc;
        }, {});
    }, [products, productObjects]);

    const productsBase = productObjects.reduce((acc, product) => {
        if (products.includes(product.id)) {
            acc[product.short_name] = [];
        }
        return acc;
    }, {});

    useEffect(() => {
        getMarketLog(view.args);
    }, [view.args]);

    const dataByProduct = data.reduce((acc, curPeriod) => {
        const product = curPeriod.BUY.length ? curPeriod.BUY[0] : curPeriod.SELL[0];
        const productName = product.product_short_name;

        if (!presentedProducts[productName]) {
            return acc;
        }
        if (isIgnoreOrder(product)) {
            return acc;
        }

        if (acc[productName]) {
            acc[productName].push(curPeriod /*.filter(filterValidPeriods)*/);
            return acc;
        }
        acc[productName] = [curPeriod];
        return acc;
    }, productsBase);

    return (
        <div className="OnlineMarketsGridViewWrapper">
            {Object.keys(presentedProducts).map((productName, productIndex) => (
                <div key={`${productName}_${productIndex}`}>
                    <div className="OnlineMarketsGridViewItemHeader">
                        {productName} ({presentedProducts[productName].unit_abbreviation} /{' '}
                        {presentedProducts[productName].currency_id} / {presentedProducts[productName].contract_type})
                    </div>
                    <div className="OnlineMarketsGridViewItemTableHeaderWrapper">
                        <div className="OnlineMarketsGridViewItemTableHeaderItem">Period</div>
                        <div className="OnlineMarketsGridViewItemTableHeaderItem">Act</div>
                        <div className="OnlineMarketsGridViewItemTableHeaderItem">Qty</div>
                        <div className="OnlineMarketsGridViewItemTableHeaderItem">Bid</div>
                        <div className="OnlineMarketsGridViewItemTableHeaderItem">Offer</div>
                        <div className="OnlineMarketsGridViewItemTableHeaderItem">Qty</div>
                    </div>
                    {fillInPeriodGaps(presentedProducts[productName], dataByProduct[productName], num_rows).map(
                        (productPeriodContracts, contractIndex) => (
                            <GridTableProductPeriod
                                key={`${productName}_${contractIndex}`}
                                product={presentedProducts[productName]}
                                productName={productName}
                                data={productPeriodContracts}
                                placeOrder={placeOrder}
                                getOrder={getOrder}
                                callDH={callDH}
                                user={user}
                                showOrderPopup={showOrderPopup}
                                onMouseOver={onMouseOver}
                                onMouseOut={onMouseOut}
                            />
                        ),
                    )}
                </div>
            ))}
        </div>
    );
};

GridView.propTypes = {
    view: PropTypes.shape({
        args: PropTypes.shape({
            num_rows: PropTypes.number,
            include: PropTypes.shape({
                products: PropTypes.arrayOf(PropTypes.number),
            }),
        }),
    }),
    user: PropTypes.shape({}),
    data: PropTypes.arrayOf(PropTypes.shape({})),
    showOrderPopup: PropTypes.func,
    getOrder: PropTypes.func,
    placeOrder: PropTypes.func,
    callDH: PropTypes.func,
    getMarketLog: PropTypes.func,
    requestStatusbarData: PropTypes.func,
    hideStatusbar: PropTypes.func,
    showStatusbar: PropTypes.func,
    // todo
};

GridView.defaultProps = {
    // todo
};

const mapStateToProps = state => ({
    // todo
});

const mapDispatchToProps = dispatch => ({
    // todo
});

export { GridView };



// WEBPACK FOOTER //
// ./source/components/OnlineMarketsGridView.js